/**
 * Atlas-backed preview helpers for the Village Maps admin editor.
 * Mirrors outdoor bake ground/overlay sprite picks from learn-hmong
 * (simplified path/water masks; forage uses ready sprites).
 * Interior helpers mirror bakeHouse in villageInteriors.ts.
 *
 * Overlay draw scales must stay in sync with VillageCanvas.tsx
 * (TREE / TALL_TREE / WELL / STALL / BIKE_DRAW_SCALE / HOUSE_DRAW_SCALE).
 */

import type { InteriorKit } from '@/lib/admin-types';

export type SpriteRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type SpriteRectsManifest = {
  cell: number;
  atlasSize: { width: number; height: number };
  sprites: Record<string, SpriteRect>;
};

/** Round canopy trees — ~2.25×2.25 tiles, bottom-anchored (matches game). */
export const TREE_DRAW_SCALE = 2.25;
/** Columnar SEA trees — slightly taller than round canopy (matches game). */
export const TALL_TREE_DRAW_SCALE = 2.5;
/** Town well landmark — ~2× tile, bottom-anchored (matches game). */
export const WELL_DRAW_SCALE = 2;
/** Interior fireplace — ~2× tile, bottom-anchored (matches VillageInteriorCanvas). */
export const HEARTH_DRAW_SCALE = 2;
/**
 * Roof L/R end-caps — slight eave lip past the wall column
 * (matches VillageCanvas ROOF_EAVE_SCALE).
 */
export const ROOF_EAVE_SCALE = 1.12;
/**
 * Outdoor house / shop / school façades draw as one stamp at this scale
 * (matches learn-hmong villageHouseStamps HOUSE_DRAW_SCALE). Must stay 1
 * so slots are integer pixels. Collision stays on the authored 7×4 / 3×4
 * footprint. Draw size is grown by HOUSE_SEAM_OVERLAP — see houseSpriteDrawScale.
 */
export const HOUSE_DRAW_SCALE = 1;

/** Extra world px on each house cell's draw size (matches the mobile canvas). */
export const HOUSE_SEAM_OVERLAP = 2;

export function houseSpriteDrawScale(
  cellSize: number,
  eaveScale = 1,
): number {
  return (
    HOUSE_DRAW_SCALE *
    eaveScale *
    ((cellSize + HOUSE_SEAM_OVERLAP) / cellSize)
  );
}
/** Market stall landmark — ~2× tile, bottom-anchored (matches game). */
export const STALL_DRAW_SCALE = 2;
/**
 * Parked bike — ~1.45× a world tile (matches VillageCanvas).
 * `drawSprite` already maps the 96×96 hi-res cell onto one tile, so this
 * is the on-screen scale (game multiplies by PLAYER_DRAW_SCALE because it
 * blits the 96px cell in 48px world units).
 */
export const BIKE_DRAW_SCALE = 1.45;
/** Farm bike rack — matches learn-hmong VillageCanvas BIKE_RACK_DRAW_SCALE. */
export const BIKE_RACK_DRAW_SCALE = 1.55;

const TREE_SPRITE_NAMES = new Set([
  'tree',
  'tree2',
  'tree3',
  'bananaTree',
  'bamboo',
  'bamboo2',
  'bamboo3',
  'palmTree',
  'mangoTree',
  'papayaTree',
  'banyanTree',
]);

const TALL_TREE_SPRITE_NAMES = new Set(['palmTree', 'papayaTree']);
/** Matches learn-hmong VillageCanvas BAMBOO_DRAW_SCALE. */
const BAMBOO_DRAW_SCALE = 2.15;
const BAMBOO_SPRITE_NAMES = new Set(['bamboo', 'bamboo2', 'bamboo3']);

/** Draw scale for an overlay sprite name (1 for props / stump / bushes). */
/** Left roof end-cap (`roofL`, `roofL2`… — not `roofML`). */
export function isRoofLeftEdgeSprite(spriteName: string): boolean {
  return spriteName === 'roofL' || /^roofL\d+$/.test(spriteName);
}

/** Right roof end-cap (`roofR`, `roofR2`… — not `roofMR`). */
export function isRoofRightEdgeSprite(spriteName: string): boolean {
  return spriteName === 'roofR' || /^roofR\d+$/.test(spriteName);
}

/** Outdoor façade cells (not interior `wallInt*` kits). */
export function isBuildingOverlaySprite(spriteName: string): boolean {
  return /^(roof|wall|door)/.test(spriteName) && !spriteName.startsWith('wallInt');
}

export type HouseStampCell = { x: number; y: number };

export type HouseStamp = {
  cells: HouseStampCell[];
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

export type DrawSpriteSlot = {
  originX: number;
  originY: number;
  slot: number;
};

function houseCellKey(x: number, y: number): string {
  return `${x},${y}`;
}

/** 4-connected groups of building tiles — one stamp per house. */
export function groupHouseStamps(cells: HouseStampCell[]): HouseStamp[] {
  const positions = new Set<string>();
  for (const cell of cells) positions.add(houseCellKey(cell.x, cell.y));
  const visited = new Set<string>();
  const stamps: HouseStamp[] = [];
  for (const start of positions) {
    if (visited.has(start)) continue;
    const [sx0, sy0] = start.split(',').map(Number);
    const queue = [start];
    visited.add(start);
    const stampCells: HouseStampCell[] = [];
    let minX = sx0;
    let maxX = sx0;
    let minY = sy0;
    let maxY = sy0;
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const [x, y] = cur.split(',').map(Number);
      stampCells.push({ x, y });
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ] as const) {
        const next = houseCellKey(x + dx, y + dy);
        if (!visited.has(next) && positions.has(next)) {
          visited.add(next);
          queue.push(next);
        }
      }
    }
    stamps.push({ cells: stampCells, minX, maxX, minY, maxY });
  }
  return stamps;
}

export function houseStampSlot(
  stamp: HouseStamp,
  x: number,
  y: number,
  cellSize: number,
  scale = HOUSE_DRAW_SCALE,
): DrawSpriteSlot {
  const cols = stamp.maxX - stamp.minX + 1;
  const rows = stamp.maxY - stamp.minY + 1;
  const slot = cellSize * scale;
  const originX = stamp.minX * cellSize + (cols * cellSize * (1 - scale)) / 2;
  const originY = (stamp.maxY + 1) * cellSize - rows * slot;
  return {
    originX: originX + (x - stamp.minX) * slot,
    originY: originY + (y - stamp.minY) * slot,
    slot,
  };
}

export function houseStampSlotMap(
  cells: HouseStampCell[],
  cellSize: number,
  scale = HOUSE_DRAW_SCALE,
): Map<string, DrawSpriteSlot> {
  const map = new Map<string, DrawSpriteSlot>();
  for (const stamp of groupHouseStamps(cells)) {
    for (const cell of stamp.cells) {
      map.set(houseCellKey(cell.x, cell.y), houseStampSlot(stamp, cell.x, cell.y, cellSize, scale));
    }
  }
  return map;
}

export function overlayDrawScale(spriteName: string): number {
  if (spriteName === 'well') return WELL_DRAW_SCALE;
  if (spriteName === 'stall') return STALL_DRAW_SCALE;
  if (spriteName === 'bike') return BIKE_DRAW_SCALE;
  if (spriteName === 'bikeRack') return BIKE_RACK_DRAW_SCALE;
  if (spriteName === 'hearth') return HEARTH_DRAW_SCALE;
  if (
    isRoofLeftEdgeSprite(spriteName) ||
    isRoofRightEdgeSprite(spriteName)
  ) {
    return ROOF_EAVE_SCALE;
  }
  if (BAMBOO_SPRITE_NAMES.has(spriteName)) return BAMBOO_DRAW_SCALE;
  if (TALL_TREE_SPRITE_NAMES.has(spriteName)) return TALL_TREE_DRAW_SCALE;
  if (TREE_SPRITE_NAMES.has(spriteName)) return TREE_DRAW_SCALE;
  return 1;
}

const OVERLAY_BY_CHAR: Record<string, string | ((x: number, y: number) => string)> = {
  '*': 'sunflower',
  ':': 'roses',
  '|': 'lilyOfTheValley',
  '✿': 'tulips',
  '❀': 'tulipsWhite',
  '❁': 'tulipsYellow',
  T: (x, y) => {
    const v = (x * 17 + y * 31) % 3;
    if (v === 1) return 'tree2';
    if (v === 2) return 'tree3';
    return 'tree';
  },
  t: 'stump',
  '1': 'bananaTree',
  '2': (x, y) => ((x * 13 + y * 29) % 3 === 0 ? 'bamboo2' : 'bamboo'),
  '3': 'palmTree',
  '4': 'mangoTree',
  '5': 'papayaTree',
  '6': 'banyanTree',
  '7': 'benchDown',
  '8': 'benchLeft',
  '9': 'benchRight',
  // F uses neighbor-mask autotiles via overlaySpriteName (fence_0…fence_15).
  "'": 'fenceTall',
  '`': 'fenceDoorLocked',
  '[': 'roofL',
  l: 'roofML',
  '†': 'roofMML',
  '^': 'roofM',
  '‡': 'roofMMR',
  f: 'roofMR',
  ']': 'roofR',
  U: 'wallUpper',
  α: 'wallUpperL',
  β: 'wallUpperML',
  'Ђ': 'wallUpperMML',
  γ: 'wallUpperM',
  'Ѓ': 'wallUpperMMR',
  δ: 'wallUpperMR',
  ε: 'wallUpperR',
  H: 'wall',
  ζ: 'wallL',
  η: 'wallML',
  'Є': 'wallMML',
  'Љ': 'wallMMR',
  θ: 'wallMR',
  ι: 'wallR',
  O: 'wallLow',
  κ: 'wallLowL',
  λ: 'wallLowML',
  'Њ': 'wallLowMML',
  'Ћ': 'wallLowMMR',
  μ: 'wallLowMR',
  ν: 'wallLowR',
  Q: 'doorTop',
  D: 'door',
  '{': 'roofL2',
  $: 'roofML2',
  '•': 'roofMML2',
  '~': 'roofM2',
  '‰': 'roofMMR2',
  '%': 'roofMR2',
  '}': 'roofR2',
  V: 'wallUpper2',
  ξ: 'wallUpperL2',
  ο: 'wallUpperML2',
  'Ќ': 'wallUpperMML2',
  π: 'wallUpperM2',
  'Ѝ': 'wallUpperMMR2',
  ρ: 'wallUpperMR2',
  ς: 'wallUpperR2',
  h: 'wall2',
  τ: 'wallL2',
  υ: 'wallML2',
  'Ў': 'wallMML2',
  'Џ': 'wallMMR2',
  φ: 'wallMR2',
  χ: 'wallR2',
  '0': 'wallLow2',
  ψ: 'wallLowL2',
  ω: 'wallLowML2',
  'Б': 'wallLowMML2',
  'Г': 'wallLowMMR2',
  À: 'wallLowMR2',
  Á: 'wallLowR2',
  y: 'doorTop2',
  e: 'door2',
  '(': 'roofL3',
  '&': 'roofML3',
  '¤': 'roofMML3',
  _: 'roofM3',
  '¥': 'roofMMR3',
  '+': 'roofMR3',
  ')': 'roofR3',
  x: 'wallUpper3',
  Â: 'wallUpperL3',
  Ã: 'wallUpperML3',
  'Д': 'wallUpperMML3',
  Ä: 'wallUpperM3',
  'Ж': 'wallUpperMMR3',
  Å: 'wallUpperMR3',
  Æ: 'wallUpperR3',
  q: 'wall3',
  Ç: 'wallL3',
  È: 'wallML3',
  'З': 'wallMML3',
  'И': 'wallMMR3',
  É: 'wallMR3',
  Ê: 'wallR3',
  ';': 'wallLow3',
  Ë: 'wallLowL3',
  Ì: 'wallLowML3',
  'Й': 'wallLowMML3',
  'К': 'wallLowMMR3',
  Í: 'wallLowMR3',
  Î: 'wallLowR3',
  a: 'doorTop3',
  o: 'door3',
  '<': 'roofL4',
  '=': 'roofM4',
  '>': 'roofR4',
  p: 'wallUpper4',
  w: 'wall4',
  '‹': 'wallLow4',
  '›': 'wallLow4',
  z: 'doorTop4',
  Z: 'door4',
  E: 'roofL5',
  G: 'roofM5',
  I: 'roofR5',
  r: 'wallUpper5',
  s: 'wall5',
  σ: 'wallLow5',
  g: 'doorTop5',
  d: 'door5',
  S: 'stall',
  L: 'well',
  '!': 'sign',
  '?': 'tipSign',
  '✉': 'mailbox',
  K: 'boatL',
  k: 'boatR',
  '≠': 'bridgeBroken',
  X: 'rockFull',
  A: 'mountPeak',
  '◈': 'cairn',
  b: 'bike',
  '/': 'bikeRack',
  j: 'mineEntranceTL',
  J: 'mineEntranceTM',
  i: 'mineEntranceTR',
  n: 'mineEntranceL',
  N: 'mineEntrance',
  u: 'mineEntranceR',
  R: 'berryBushFull',
  Y: 'fruitTreeFull',
};

function neighborMask(
  rows: string[],
  mapW: number,
  mapH: number,
  x: number,
  y: number,
  chars: string,
): number {
  const connected = (nx: number, ny: number): boolean => {
    if (nx < 0 || ny < 0 || nx >= mapW || ny >= mapH) return true;
    return chars.includes(rows[ny]?.[nx] ?? '');
  };
  return (
    (connected(x, y - 1) ? 1 : 0) |
    (connected(x + 1, y) ? 2 : 0) |
    (connected(x, y + 1) ? 4 : 0) |
    (connected(x - 1, y) ? 8 : 0)
  );
}

/** 8-neighbor mask (cardinals + diagonals) — matches villageOutdoorMaps. */
function neighborMask8(
  rows: string[],
  mapW: number,
  mapH: number,
  x: number,
  y: number,
  chars: string,
): number {
  const connected = (nx: number, ny: number): boolean => {
    if (nx < 0 || ny < 0 || nx >= mapW || ny >= mapH) return true;
    return chars.includes(rows[ny]?.[nx] ?? '');
  };
  return (
    (connected(x, y - 1) ? 1 : 0) |
    (connected(x + 1, y) ? 2 : 0) |
    (connected(x, y + 1) ? 4 : 0) |
    (connected(x - 1, y) ? 8 : 0) |
    (connected(x - 1, y - 1) ? 16 : 0) |
    (connected(x + 1, y - 1) ? 32 : 0) |
    (connected(x + 1, y + 1) ? 64 : 0) |
    (connected(x - 1, y + 1) ? 128 : 0)
  );
}

/** Cliff lip / body / base from N/S neighbors (matches villageOutdoorMaps). */
function cliffSpriteName(
  rows: string[],
  _mapW: number,
  mapH: number,
  x: number,
  y: number,
): string {
  const above = y > 0 ? (rows[y - 1]?.[x] ?? 'T') : 'T';
  const below = y < mapH - 1 ? (rows[y + 1]?.[x] ?? 'T') : 'T';
  const lipAbove = 'mPv.*?:|✿❀❁?,XA'.includes(above);
  if (lipAbove || (above !== 'C' && 'mvP'.includes(above))) {
    return 'cliffFaceTop';
  }
  if (below !== 'C') {
    return 'cliffFaceBase';
  }
  return (x + y) % 5 === 0 ? 'cliffFaceAlt' : 'cliffFace';
}

function pathNeighborChars(areaId: string): string {
  // Tip/sign/mailbox posts (`?`/`!`/`✉`) count as path so underlays join the spine.
  return areaId === 'mountain' ? 'PBmvCXANnujJi?!✉' : 'PB?!✉';
}

/**
 * Ground under transparent prop overlays (`?` tip, `!` sign).
 * Matches learn-hmong propGroundSpriteFor: skip scenic props so path+bamboo
 * tips inherit path, not grass.
 */
function propGroundSpriteName(
  rows: string[],
  mapW: number,
  mapH: number,
  x: number,
  y: number,
  areaId: string,
): string {
  const n = (dx: number, dy: number): string => {
    const nx = x + dx;
    const ny = y + dy;
    if (nx < 0 || ny < 0 || nx >= mapW || ny >= mapH) return '.';
    return rows[ny]?.[nx] ?? '.';
  };
  for (const [dx, dy] of [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ] as const) {
    const chN = n(dx, dy);
    if (chN === 'B' || chN === '≠' || chN === 'K') return 'bridge';
    if (chN === 'P') {
      return `path_${neighborMask(rows, mapW, mapH, x, y, pathNeighborChars(areaId))}`;
    }
    if (chN === 'm' || chN === 'X' || chN === 'A') return 'mountDirt';
    if (chN === 'C') {
      return cliffSpriteName(rows, mapW, mapH, x, y);
    }
    if (
      chN === 'v' ||
      chN === 'N' ||
      chN === 'n' ||
      chN === 'u' ||
      chN === 'j' ||
      chN === 'J' ||
      chN === 'i'
    ) {
      return 'mountLow';
    }
    if (chN === '#') return 'grass';
    if (chN === 'c') return 'cobble';
    if (chN === 'M') return 'paddy';
    if (chN === 'W' || chN === 'k') {
      return `water_${neighborMask(rows, mapW, mapH, x, y, 'WBk@≠?!')}_0`;
    }
    if (chN === '@') {
      return `waterDeep_${neighborMask8(rows, mapW, mapH, x, y, '@')}_0`;
    }
  }
  return 'grass';
}

export function groundSpriteName(
  rows: string[],
  mapW: number,
  mapH: number,
  ch: string,
  x: number,
  y: number,
  areaId: string,
): string {
  switch (ch) {
    case ',':
      return 'grass2';
    case '*':
    case ':':
    case '|':
    case '✿':
    case '❀':
    case '❁':
      // Flower clumps are overlays; ground stays grass under them.
      return 'grass';
    case 'P':
      return `path_${neighborMask(rows, mapW, mapH, x, y, pathNeighborChars(areaId))}`;
    case 'W':
    case 'k':
      return `water_${neighborMask(rows, mapW, mapH, x, y, 'WBk@≠?!')}_0`;
    case '@':
      return `waterDeep_${neighborMask8(rows, mapW, mapH, x, y, '@')}_0`;
    case 'M':
      return 'paddy';
    case '#':
      // Runtime plot overlays draw untilled/tilled/wet soil; ground is grass.
      return 'grass';
    case 'B':
      return 'bridge';
    case '≠':
      // Broken span: water underlay; planks are overlay (matches app bake).
      return `water_${neighborMask(rows, mapW, mapH, x, y, 'WBk@≠?!')}_0`;
    case 'K':
      // Bow sits on dock planks (matches app bake).
      return 'bridge';
    case 'm':
      return 'mountDirt';
    case 'v':
      return 'mountLow';
    case 'c':
      return 'cobble';
    case 'C':
      return cliffSpriteName(rows, mapW, mapH, x, y);
    case 'N':
    case 'n':
    case 'u':
    case 'j':
    case 'J':
    case 'i':
      return 'mountLow';
    case 'X':
    case 'A':
      return 'mountDirt';
    case '?':
    case '!':
    case '✉':
      return propGroundSpriteName(rows, mapW, mapH, x, y, areaId);
    default:
      return 'grass';
  }
}

export function overlaySpriteName(
  ch: string,
  x: number,
  y: number,
  rows?: string[],
  mapW?: number,
  mapH?: number,
): string | null {
  if (ch === 'F') {
    if (rows && mapW != null && mapH != null) {
      // Join rails through fence gates (`-`), matching mobile bake.
      return `fence_${neighborMask(rows, mapW, mapH, x, y, 'F-')}`;
    }
    return 'fence_0';
  }
  if (ch === '-') return 'fenceGate';
  if (ch === 'C') {
    // A few cliff faces get a draped hanging-vine overlay (matches app bake).
    return (x * 5 + y * 3) % 13 === 0 ? 'cliffVine' : null;
  }
  const entry = OVERLAY_BY_CHAR[ch];
  if (!entry) return null;
  return typeof entry === 'function' ? entry(x, y) : entry;
}

/** Interior furniture / prop char → atlas sprite name (bakeHouse PROP_SPRITE). */
const INTERIOR_PROP_SPRITE: Record<string, string> = {
  t: 'table',
  c: 'chair',
  s: 'shelf',
  h: 'hearth',
  x: 'chest',
  o: 'pot',
  l: 'lantern',
  j: 'journal',
  k: 'stick',
  f: 'cloth',
  p: 'seedSack',
  r: 'chiliString',
  m: 'farmTools',
  a: 'wallHanging',
  A: 'wallHanging2',
  d: 'cabinet',
  v: 'stove',
  g: 'waterJug',
  n: 'nightstand',
  q: 'shelfWall',
  y: 'benchL',
  z: 'benchR',
};

function isInteriorWallChar(ch: string): boolean {
  return ch === 'U' || ch === 'w';
}

function kitFloorSprite(kit: InteriorKit): string {
  if (kit === 'moss') return 'floorMoss';
  if (kit === 'slate') return 'floorSlate';
  return 'floorWood';
}

function kitWallSprite(kit: InteriorKit, top: boolean): string {
  if (kit === 'moss') return top ? 'wallIntMossTop' : 'wallIntMoss';
  if (kit === 'slate') return top ? 'wallIntSlateTop' : 'wallIntSlate';
  return top ? 'wallIntGoldTop' : 'wallIntGold';
}

function kitRugSprite(kit: InteriorKit): string {
  if (kit === 'moss') return 'rugMoss';
  if (kit === 'slate') return 'rugSlate';
  return 'rugGold';
}

function kitRugLargeSprite(kit: InteriorKit): string {
  if (kit === 'moss') return 'rugLargeMoss';
  if (kit === 'slate') return 'rugLargeSlate';
  return 'rugLargeGold';
}

/**
 * Ground sprite under an interior cell (kit floor under walls/furniture/
 * rugs; exit mat for E). Matches bakeHouse groundSprites — rugs are
 * overlays, not ground stamps.
 */
export function interiorGroundSprite(
  ch: string,
  kit: InteriorKit,
  floorKit: InteriorKit = kit,
): string {
  if (ch === 'E') return 'exitMat';
  return kitFloorSprite(floorKit);
}

/** Dining stamps the rug should sit under (matches villageInteriors). */
const DINING_CHARS = new Set(['t', 'c', 'y', 'z']);

/**
 * Grow a rug bbox through orthogonally adjacent table / bench / chair cells
 * so the overlay draws under the dining set (map cells stay furniture).
 */
export function expandRugOverDining(
  blob: { x: number; y: number; w: number; h: number },
  rows: string[],
): { x: number; y: number; w: number; h: number } {
  const mapH = rows.length;
  const mapW = rows[0]?.length ?? 0;
  let minX = blob.x;
  let minY = blob.y;
  let maxX = blob.x + blob.w - 1;
  let maxY = blob.y + blob.h - 1;
  let changed = true;
  while (changed) {
    changed = false;
    for (let y = Math.max(0, minY - 1); y <= Math.min(mapH - 1, maxY + 1); y++) {
      for (let x = Math.max(0, minX - 1); x <= Math.min(mapW - 1, maxX + 1); x++) {
        const inside = x >= minX && x <= maxX && y >= minY && y <= maxY;
        if (inside) continue;
        const adj =
          (y >= minY && y <= maxY && (x === minX - 1 || x === maxX + 1)) ||
          (x >= minX && x <= maxX && (y === minY - 1 || y === maxY + 1));
        if (!adj || !DINING_CHARS.has(rows[y]?.[x] ?? '')) continue;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
        changed = true;
      }
    }
  }
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Contiguous `~` blobs → one rug overlay (mirrors mobile findRugBlobs). */
export function findInteriorRugBlobs(
  rows: string[],
): { x: number; y: number; w: number; h: number }[] {
  const mapH = rows.length;
  const mapW = rows[0]?.length ?? 0;
  const visited = new Set<string>();
  const blobs: { x: number; y: number; w: number; h: number }[] = [];

  const tileAt = (x: number, y: number): string => {
    if (x < 0 || y < 0 || x >= mapW || y >= mapH) return 'U';
    return rows[y]?.[x] ?? 'U';
  };

  for (let y = 0; y < mapH; y += 1) {
    for (let x = 0; x < mapW; x += 1) {
      const key = `${x},${y}`;
      if (tileAt(x, y) !== '~' || visited.has(key)) continue;
      let minX = x;
      let minY = y;
      let maxX = x;
      let maxY = y;
      const stack = [{ x, y }];
      visited.add(key);
      while (stack.length) {
        const cur = stack.pop()!;
        minX = Math.min(minX, cur.x);
        minY = Math.min(minY, cur.y);
        maxX = Math.max(maxX, cur.x);
        maxY = Math.max(maxY, cur.y);
        for (const [dx, dy] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          const nx = cur.x + dx;
          const ny = cur.y + dy;
          const nk = `${nx},${ny}`;
          if (tileAt(nx, ny) !== '~' || visited.has(nk)) continue;
          visited.add(nk);
          stack.push({ x: nx, y: ny });
        }
      }
      blobs.push({
        x: minX,
        y: minY,
        w: maxX - minX + 1,
        h: maxY - minY + 1,
      });
    }
  }
  return blobs;
}

export type InteriorRugOverlay = {
  x: number;
  y: number;
  w: number;
  h: number;
  sprite: string;
};

/** One rug overlay per contiguous `~` blob (matches bakeHouse). */
export function interiorRugOverlays(
  rows: string[],
  kit: InteriorKit,
  rugKit: InteriorKit = kit,
): InteriorRugOverlay[] {
  return findInteriorRugBlobs(rows).map((raw) => {
    const blob = expandRugOverDining(raw, rows);
    const multi = blob.w > 1 || blob.h > 1;
    return {
      ...blob,
      sprite: multi ? kitRugLargeSprite(rugKit) : kitRugSprite(rugKit),
    };
  });
}

/**
 * Overlay sprite names for an interior cell (wall + window, bed head/foot,
 * furniture/props). Matches bakeHouse overlayCells order.
 */
export function interiorOverlaySprites(
  ch: string,
  x: number,
  y: number,
  rows: string[],
  kit: InteriorKit,
): string[] {
  const mapH = rows.length;
  const mapW = rows[0]?.length ?? 0;
  const tileAt = (tx: number, ty: number): string => {
    if (tx < 0 || ty < 0 || tx >= mapW || ty >= mapH) return 'U';
    return rows[ty]?.[tx] ?? 'U';
  };

  const overlays: string[] = [];
  if (isInteriorWallChar(ch)) {
    const top = y === 0 || !isInteriorWallChar(tileAt(x, y - 1));
    overlays.push(kitWallSprite(kit, top));
    if (ch === 'w') overlays.push('window');
    return overlays;
  }
  if (ch === 'b') {
    const belowIsBed = tileAt(x, y + 1) === 'b';
    const aboveIsBed = tileAt(x, y - 1) === 'b';
    overlays.push(belowIsBed ? 'bedHead' : aboveIsBed ? 'bedFoot' : 'bed');
    return overlays;
  }
  const prop = INTERIOR_PROP_SPRITE[ch];
  if (prop) overlays.push(prop);
  return overlays;
}

/** Representative ground + overlays for an interior palette swatch. */
export function interiorPalettePreviewSprites(
  ch: string,
  kit: InteriorKit,
  floorKit: InteriorKit = kit,
  rugKit: InteriorKit = kit,
): { ground: string; overlays: string[] } {
  // Sample 2-row bed / wall-top for palette fidelity.
  const sampleRows =
    ch === 'b'
      ? ['b', 'b']
      : ch === 'w' || ch === 'U'
        ? [ch, ch]
        : [ch];
  const sampleY = ch === 'b' ? 0 : 0;
  // Rug cells use kit floor underlay + rug overlay (not a ground stamp).
  if (ch === '~') {
    return {
      ground: kitFloorSprite(floorKit),
      overlays: [kitRugLargeSprite(rugKit)],
    };
  }
  return {
    ground: interiorGroundSprite(ch, kit, floorKit),
    overlays: interiorOverlaySprites(ch, 0, sampleY, sampleRows, kit),
  };
}

/**
 * Draw an atlas sprite into a map cell.
 * `drawScale` enlarges the sprite around a bottom-center foot anchor
 * (matches VillageCanvas `scaledTileXform`). Used for trees (~2.25×),
 * well / stall (~2×); scaled art spills into neighboring tiles.
 *
 * `eaveSide`: roof L/R end-caps pin the outer edge so the eave spills
 * past the wall column (matches VillageCanvas `roofEaveTileXform`).
 * `slot`: optional inset destination (house stamps at HOUSE_DRAW_SCALE).
 */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  atlas: HTMLImageElement,
  rect: SpriteRect,
  cellX: number,
  cellY: number,
  cellSize: number,
  drawScale = 1,
  eaveSide: 'left' | 'right' | null = null,
  slot?: DrawSpriteSlot,
): void {
  const unit = cellSize / rect.width;
  const drawW = rect.width * unit * drawScale;
  const drawH = rect.height * unit * drawScale;
  const ox = slot?.originX ?? cellX * cellSize;
  const oy = slot?.originY ?? cellY * cellSize;
  const slotSize = slot?.slot ?? cellSize;
  let dx: number;
  if (eaveSide === 'left') {
    // Pin right edge to cell right; spill left.
    dx = ox + slotSize - drawW;
  } else if (eaveSide === 'right') {
    // Pin left edge to cell left; spill right.
    dx = ox;
  } else {
    // Bottom-center anchor so scaled trees spill upward / sideways like the game.
    dx = ox + (slotSize - drawW) / 2;
  }
  const dy = oy + slotSize - drawH;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(
    atlas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    dx,
    dy,
    drawW,
    drawH,
  );
}

/** Stretch a sprite across a multi-tile footprint (interior rug blobs). */
export function drawSpriteFootprint(
  ctx: CanvasRenderingContext2D,
  atlas: HTMLImageElement,
  rect: SpriteRect,
  cellX: number,
  cellY: number,
  cellSize: number,
  cellsW: number,
  cellsH: number,
): void {
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(
    atlas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    cellX * cellSize,
    cellY * cellSize,
    cellsW * cellSize,
    cellsH * cellSize,
  );
}

/** Representative ground + overlay sprite names for a palette swatch. */
export function palettePreviewSprites(ch: string): {
  ground: string;
  overlay: string | null;
} {
  const rows = [ch];
  let ground = groundSpriteName(rows, 1, 1, ch, 0, 0, 'town');
  // Prefer a filled autotile sample in the palette (all neighbors connected).
  if (ch === 'P') ground = 'path_15';
  if (ch === 'W' || ch === 'k') ground = 'water_15_0';
  if (ch === '@') ground = 'waterDeep_0';
  let overlay = overlaySpriteName(ch, 0, 0);
  if (ch === 'F') overlay = 'fence_15';
  if (ch === '-') overlay = 'fenceGate';
  return { ground, overlay };
}

/** Draw a single palette cell (ground + optional overlay) into a square canvas. */
export function drawPaletteSwatch(
  ctx: CanvasRenderingContext2D,
  atlas: HTMLImageElement,
  rects: SpriteRectsManifest,
  ch: string,
  size: number,
): void {
  ctx.clearRect(0, 0, size, size);
  ctx.imageSmoothingEnabled = false;
  const { ground, overlay } = palettePreviewSprites(ch);
  const groundRect = rects.sprites[ground] ?? rects.sprites.grass;
  if (groundRect) {
    drawSprite(ctx, atlas, groundRect, 0, 0, size);
  }
  if (overlay) {
    const overlayRect = rects.sprites[overlay];
    if (overlayRect) {
      drawSprite(ctx, atlas, overlayRect, 0, 0, size);
    }
  }
}

/** Draw an interior palette cell using kit floor / wall / furniture overlays. */
export function drawInteriorPaletteSwatch(
  ctx: CanvasRenderingContext2D,
  atlas: HTMLImageElement,
  rects: SpriteRectsManifest,
  ch: string,
  size: number,
  kit: InteriorKit,
  floorKit: InteriorKit = kit,
  rugKit: InteriorKit = kit,
): void {
  ctx.clearRect(0, 0, size, size);
  ctx.imageSmoothingEnabled = false;
  const { ground, overlays } = interiorPalettePreviewSprites(
    ch,
    kit,
    floorKit,
    rugKit,
  );
  const groundRect = rects.sprites[ground] ?? rects.sprites.floorWood;
  if (groundRect) {
    drawSprite(ctx, atlas, groundRect, 0, 0, size);
  }
  for (const overlay of overlays) {
    const overlayRect = rects.sprites[overlay];
    if (overlayRect) {
      drawSprite(ctx, atlas, overlayRect, 0, 0, size);
    }
  }
}

/**
 * Fit a named atlas sprite into a square thumb (contain, bottom-centered).
 * Tall NPC cells (48×96) shrink to full height; 48×48 props fill the square.
 */
export function drawSpriteThumb(
  ctx: CanvasRenderingContext2D,
  atlas: HTMLImageElement,
  rect: SpriteRect,
  size: number,
): void {
  ctx.clearRect(0, 0, size, size);
  ctx.imageSmoothingEnabled = false;
  const scale = Math.min(size / rect.width, size / rect.height);
  const drawW = rect.width * scale;
  const drawH = rect.height * scale;
  const dx = (size - drawW) / 2;
  const dy = size - drawH;
  ctx.drawImage(
    atlas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    dx,
    dy,
    drawW,
    drawH,
  );
}

let atlasPromise: Promise<HTMLImageElement> | null = null;
let rectsPromise: Promise<SpriteRectsManifest> | null = null;

export function loadVillageAtlas(): Promise<HTMLImageElement> {
  if (!atlasPromise) {
    atlasPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load village-atlas.png'));
      // Vite serves files from /admin/ base — public assets are under that base.
      img.src = `${import.meta.env.BASE_URL}village/village-atlas.png`;
    });
  }
  return atlasPromise;
}

export function loadSpriteRects(): Promise<SpriteRectsManifest> {
  if (!rectsPromise) {
    rectsPromise = fetch(`${import.meta.env.BASE_URL}village/sprite-rects.json`).then(
      async (res) => {
        if (!res.ok) throw new Error(`sprite-rects.json ${res.status}`);
        return (await res.json()) as SpriteRectsManifest;
      },
    );
  }
  return rectsPromise;
}
